import { useEffect, useState } from "react";
import { Link } from "wouter";
import CustomLink from "./CustomLink";
import { CulturalExperience } from "../services/cultureService";
import { Skeleton } from "@/components/ui/skeleton";
import { fetchCulturalExperiences } from "../services/cultureService";

// Map of specific cultural experiences to their verified working images
const imageMap = new Map<string, string>([
  ["Turkish Hammam Experience", "/images/culture/turkishhammam.jpg"],
  ["Whirling Dervishes Ceremony", "/images/culture/whirling-dervishes.jpg"],
  ["Traditional Carpet Weaving", "https://images.unsplash.com/photo-1606744888344-493238951221?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"],
  ["Evil Eye Bead Making", "/images/culture/evileye.jpg"],
  ["Turkish Night Show", "https://images.unsplash.com/photo-1530025809667-59573e1f874d?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"],
  ["Turkish Oil Wrestling", "/images/culture/oilwrestling.jpg"]
]);

// Set priority order for cultural experiences
const priorityExperiences = [
  "Whirling Dervishes Ceremony",
  "Turkish Hammam Experience", 
  "Evil Eye Bead Making"
];

// Experiences to exclude from top 4
const excludeFromTop = [
  "Turkish Night Show"
];

// Fallback experiences with verified working images
const fallbackExperiences: CulturalExperience[] = [
  {
    id: "hammam1",
    title: "Turkish Hammam Experience",
    description: "Traditional Turkish bath experience dating back to Ottoman times.",
    category: "wellness",
    imageUrl: "https://images.unsplash.com/photo-1584622650111-993a426fbf0a?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
    content: "The Turkish hammam is a centuries-old ritual that combines cleansing with relaxation.",
    locations: ["Istanbul", "Antalya"]
  },
  {
    id: "dervish1",
    title: "Whirling Dervishes Ceremony",
    description: "Mystical Sufi ceremony featuring the famous spinning meditation.",
    category: "spiritual",
    imageUrl: "/images/culture/whirling-dervish.jpg",
    content: "The Sema ceremony represents the mystical ascent through mind and love to perfection.",
    locations: ["Konya", "Istanbul"]
  },
  {
    id: "carpet1",
    title: "Traditional Carpet Weaving",
    description: "Ancient art of Turkish carpet making with natural dyes and traditional patterns.",
    category: "crafts",
    imageUrl: "https://images.unsplash.com/photo-1606744888344-493238951221?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
    content: "Turkish carpet weaving is a tradition dating back thousands of years.",
    locations: ["Cappadocia", "Istanbul"]
  },
  {
    id: "evileye1",
    title: "Evil Eye Bead Making",
    description: "Learn about the cultural significance and creation of Nazar boncuğu.",
    category: "crafts",
    imageUrl: "https://images.unsplash.com/photo-1582998639683-64219b53db22?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
    content: "The Evil Eye is one of Turkey's most iconic symbols, believed to ward off evil.",
    locations: ["Istanbul", "Izmir"]
  }
];

export default function CulturalExperiences() {
  const [experiences, setExperiences] = useState<CulturalExperience[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadExperiences = async () => {
      try {
        setIsLoading(true);
        
        // First try to load from Firebase
        try {
          const data = await fetchCulturalExperiences();
          
          if (data && data.length > 0) {
            // Remove duplicates and enhance with proper images
            const uniqueExperiences = Array.from(
              new Map(data.map(exp => [exp.title, exp])).values()
            ).map(exp => {
              // Always use our verified images
              if (imageMap.has(exp.title)) {
                return {
                  ...exp,
                  imageUrl: imageMap.get(exp.title)!
                };
              }
              return exp;
            });
            
            // Sort experiences by priority
            const orderedExperiences = orderExperiencesByPriority(uniqueExperiences);
            setExperiences(orderedExperiences);
          } else {
            // Use fallback data if no data is retrieved
            const orderedFallbacks = orderExperiencesByPriority(fallbackExperiences);
            setExperiences(orderedFallbacks);
          }
        } catch (fbError) {
          console.error("Error fetching from Firebase:", fbError);
          // Always fall back to hardcoded data on Firebase error
          const orderedFallbacks = orderExperiencesByPriority(fallbackExperiences);
          setExperiences(orderedFallbacks);
        }
      } catch (err) {
        console.error("Error fetching cultural experiences:", err);
        setError("Failed to load cultural experiences");
        // Use fallback data on error
        const orderedFallbacks = orderExperiencesByPriority(fallbackExperiences);
        setExperiences(orderedFallbacks);
      } finally {
        setIsLoading(false);
      }
    };

    loadExperiences();
  }, []);

  // Function to order experiences by priority
  const orderExperiencesByPriority = (experienceList: CulturalExperience[]): CulturalExperience[] => {
    const orderedExperiences: CulturalExperience[] = [];
    
    // First add the priority experiences in the specified order
    for (const title of priorityExperiences) {
      const experience = experienceList.find(exp => exp.title === title);
      if (experience) {
        orderedExperiences.push(experience);
      }
    }
    
    // Then add remaining experiences, excluding those already added or in exclude list
    for (const experience of experienceList) {
      if (!priorityExperiences.includes(experience.title) && 
          !orderedExperiences.some(e => e.title === experience.title) &&
          !excludeFromTop.includes(experience.title)) {
        orderedExperiences.push(experience);
      }
    }
    
    // Finally add any excluded experiences (they'll appear after the top 4)
    for (const title of excludeFromTop) {
      const experience = experienceList.find(exp => exp.title === title);
      if (experience) {
        orderedExperiences.push(experience);
      }
    }
    
    return orderedExperiences;
  };

  if (error && experiences.length === 0) {
    return (
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-poppins font-bold text-[#333333] mb-2 text-center">Cultural Experiences</h2>
          <p className="text-center text-red-500 mb-8">Failed to load cultural experiences. Please try again later.</p>
        </div>
      </section>
    );
  }

  return (
    <section className="py-12 bg-white">
      <div className="container mx-auto px-4">
        <h2 className="text-3xl md:text-4xl font-poppins font-bold text-[#333333] mb-2 text-center">Cultural Experiences</h2>
        <p className="text-center text-gray-600 mb-8 max-w-3xl mx-auto">
          Immerse yourself in Turkey's rich cultural heritage and traditional experiences.
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {isLoading ? (
            // Skeleton loading state
            Array(4).fill(0).map((_, index) => (
              <div key={index} className="bg-[#F5F5F5] rounded-lg overflow-hidden shadow-md">
                <Skeleton className="h-48" />
                <div className="p-5">
                  <Skeleton className="h-6 w-3/4 mb-2" />
                  <Skeleton className="h-4 w-full mb-3" />
                  <Skeleton className="h-4 w-1/2" />
                </div>
              </div>
            ))
          ) : (
            // Cultural experiences cards (show top 4)
            experiences.slice(0, 4).map((experience) => (
              <div key={experience.id} className="bg-[#F5F5F5] rounded-lg overflow-hidden shadow-md group hover:shadow-lg transition duration-200">
                <div className="h-48 overflow-hidden">
                  <img
                    src={experience.imageUrl}
                    alt={experience.title}
                    loading="lazy"
                    width={400}
                    height={192}
                    className="w-full h-full object-cover group-hover:scale-105 transition duration-500"
                    onError={(e) => {
                      // Fallback image if the original fails to load
                      const target = e.target as HTMLImageElement;
                      // First try the imageMap 
                      if (imageMap.has(experience.title)) {
                        target.src = imageMap.get(experience.title)!;
                      } else {
                        // Then try the fallback experiences 
                        const fallbackExp = fallbackExperiences.find(f => f.title === experience.title);
                        if (fallbackExp) {
                          target.src = fallbackExp.imageUrl;
                        } else {
                          // Generic fallback as last resort
                          target.src = "https://images.unsplash.com/photo-1584622650111-993a426fbf0a?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80";
                        }
                      }
                    }}
                  />
                </div>
                <div className="p-5">
                  <h3 className="text-xl font-poppins font-bold text-[#333333] mb-2">{experience.title}</h3>
                  <p className="text-gray-600 mb-4">{experience.description}</p>
                  <CustomLink href={`/culture#${experience.title.toLowerCase().replace(/\s+/g, '-')}`}>
                    <span className="text-[#30D5C8] font-poppins font-semibold hover:text-[#E30A17] transition duration-200 cursor-pointer">
                      Learn More <span className="ml-1">→</span>
                    </span>
                  </CustomLink>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </section>
  );
}
